import { createSlice, PayloadAction, current } from "@reduxjs/toolkit";
import {
  ITranscriptionMessage,
  VisitType,
  ISegmentData,
  SessionStatus,
} from "../../hexagon/core/ports/HealthScribe";
import { mergeTranscriptions } from "../../util/sequenceMerge";

interface HealthScribeState {
  isActive: boolean;
  isPaused: boolean;
  isPausing: boolean; // True during the pause operation (audio stopped but Redux not yet updated)
  isHidden: boolean;
  isLoading: boolean; // Track loading state during session initialization
  visitType: VisitType;
  startTime: string | null; // ISO string for persistence
  sessionId: string | null; // Track the backend session ID
  // V2 Multi-Segment Fields
  currentSegmentId: string | null; // Current segment being recorded
  currentSegmentNumber: number; // Current segment number (1, 2, 3...)
  segments: ISegmentData[]; // Array of completed/paused segments
  currentSegmentTranscriptions: ITranscriptionMessage[]; // Transcriptions for current segment
  // End V2 Fields
  clinicalSuggestionsEnabled: boolean;
  patientId: string | number | null; // Track which patient the recording is for
  patientName: string | null; // Store patient name for display
  connectionStatus: "connected" | "disconnected" | "error" | null; // WebSocket status
  transcription: ITranscriptionMessage[]; // Use proper type from hexagon - keeps all transcriptions for display
  noteToOverwrite: string | null;
  // Draft and processing status
  isDraft: boolean; // Whether visit is in draft mode
  processingStatus: "idle" | "processing" | "completed" | "error"; // High-quality transcription processing status
  processingProgress: number; // Processing progress 0-100
  transcriptionId: number | null; // ID of the transcription record for polling
  // Multi-session visit tracking (for user's mental model of "one visit")
  hasClickedGenerateNarrative: boolean; // True after first Generate Narrative - shows "Resume" instead of "Start"
  // Heidi Integration Fields
  transcriptionSource: "aws" | "heidi" | null; // Which STT backend is being used
  heidiSessionId: string | null; // Heidi's session ID (different from our internal sessionId)
  heidiTranscriptStatus:
    | "idle" // No active Heidi session
    | "waiting" // Recording active, waiting for first upload
    | "processing" // Uploading audio chunk
    | "transcribing" // Recording paused, waiting for backend to return transcripts
    | "received" // Transcripts received
    | "error"; // Upload/transcription error
  lastTranscriptUpdateTime: string | null; // When we last received transcript data
}

const initialState: HealthScribeState = {
  isActive: false,
  isPaused: false,
  isPausing: false,
  isHidden: false,
  isLoading: false,
  visitType: "FOLLOW_UP",
  startTime: null,
  sessionId: null,
  // V2 Multi-Segment Initial Values
  currentSegmentId: null,
  currentSegmentNumber: 1,
  segments: [],
  currentSegmentTranscriptions: [],
  // End V2 Initial Values
  clinicalSuggestionsEnabled: true,
  patientId: null,
  patientName: null,
  connectionStatus: null,
  transcription: [],
  noteToOverwrite: null,
  isDraft: false,
  processingStatus: "idle",
  processingProgress: 0,
  transcriptionId: null,
  hasClickedGenerateNarrative: false,
  // Heidi Integration Initial Values
  transcriptionSource: null,
  heidiSessionId: null,
  heidiTranscriptStatus: "idle",
  lastTranscriptUpdateTime: null,
};

const healthScribeSlice = createSlice({
  name: "healthScribe",
  initialState,
  reducers: {
    // New action to immediately show UI with loading state
    initializeHealthScribe: (
      state,
      action: PayloadAction<{
        visitType: VisitType;
        patientId?: string | number;
        patientName?: string;
      }>
    ) => {
      state.isActive = true;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = true;
      state.visitType = action.payload.visitType;
      state.patientId = action.payload.patientId || null;
      state.patientName = action.payload.patientName || null;
      // Don't set startTime here - it will be set by startSegment when recording actually starts
      // state.startTime = new Date().toISOString();
      state.sessionId = null;
      state.currentSegmentId = null; // FIX: Clear stale segment ID from previous session
      state.connectionStatus = null;
      state.transcription = [];
    },

    startHealthScribe: (
      state,
      action: PayloadAction<{
        visitType: VisitType;
        sessionId: string;
        patientId?: string | number;
        patientName?: string;
      }>
    ) => {
      state.isActive = true;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.visitType = action.payload.visitType;
      state.sessionId = action.payload.sessionId;
      state.patientId = action.payload.patientId || null;
      state.patientName = action.payload.patientName || null;
      state.startTime = new Date().toISOString();
      state.connectionStatus = null;
      state.transcription = [];
    },

    // Action to handle initialization failure
    healthScribeInitializationFailed: (state) => {
      state.isActive = false;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.startTime = null;
      state.sessionId = null;
      state.currentSegmentId = null; // FIX: Clear stale segment ID on failure
      state.patientId = null;
      state.patientName = null;
      state.connectionStatus = null;
      state.transcription = [];
    },

    setIsPaused: (state, action: PayloadAction<boolean>) => {
      state.isPaused = action.payload;
    },

    stopHealthScribe: (
      state,
      action: PayloadAction<{
        noteToOverwrite: string;
        isDraft?: boolean;
        transcriptionId?: number;
      }>
    ) => {
      state.isActive = false;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.startTime = null;
      state.sessionId = null;
      state.patientId = null;
      state.patientName = null;
      state.connectionStatus = null;
      state.noteToOverwrite = action.payload.noteToOverwrite;
      state.isDraft = action.payload.isDraft ?? true; // Default to draft
      state.transcriptionId = action.payload.transcriptionId ?? null;
      state.processingStatus = action.payload.isDraft ? "idle" : "processing"; // If not draft, backend will process (~20 min)
      // Note: Keep transcription data until explicitly cleared for final processing
    },

    pauseAndUploadHealthScribe: (
      state,
      action: PayloadAction<{
        noteToOverwrite: string;
        isDraft?: boolean;
        transcriptionId?: number;
      }>
    ) => {
      // Pause recording but keep everything else intact (like pause button)
      state.isPaused = true;
      state.isLoading = false;
      state.noteToOverwrite = action.payload.noteToOverwrite;
      state.isDraft = action.payload.isDraft ?? true; // Default to draft
      state.transcriptionId = action.payload.transcriptionId ?? null;
      state.processingStatus = action.payload.isDraft ? "idle" : "processing";
      // Keep isActive, startTime, sessionId, patientId, patientName, connectionStatus, and transcription data
    },

    hideHealthScribe: (state) => {
      state.isHidden = true;
    },

    showHealthScribe: (state) => {
      state.isHidden = false;
    },

    setVisitType: (state, action: PayloadAction<VisitType>) => {
      state.visitType = action.payload;
    },

    setPatientInfo: (
      state,
      action: PayloadAction<{
        patientId: string | number;
        patientName?: string;
      }>
    ) => {
      state.patientId = action.payload.patientId;
      if (action.payload.patientName) {
        state.patientName = action.payload.patientName;
      }
    },

    toggleClinicalSuggestions: (state, action: PayloadAction<boolean>) => {
      state.clinicalSuggestionsEnabled = action.payload;
    },

    setConnectionStatus: (
      state,
      action: PayloadAction<"connected" | "disconnected" | "error">
    ) => {
      state.connectionStatus = action.payload;
    },

    addTranscription: (state, action: PayloadAction<ITranscriptionMessage>) => {
      state.transcription.push(action.payload);
    },

    updateTranscription: (
      state,
      action: PayloadAction<ITranscriptionMessage>
    ) => {
      const existingIndex = state.transcription.findIndex(
        (msg) => msg.id === action.payload.id
      );
      if (existingIndex !== -1) {
        // Update existing message
        state.transcription[existingIndex] = action.payload;
      } else {
        // Add new message if not found
        state.transcription.push(action.payload);
      }
    },

    clearTranscription: (state) => {
      state.transcription = [];
    },

    setNoteToOverwrite: (state, action: PayloadAction<string | null>) => {
      state.noteToOverwrite = action.payload;
    },

    setProcessingStatus: (
      state,
      action: PayloadAction<{
        status: "idle" | "processing" | "completed" | "error";
        progress?: number;
      }>
    ) => {
      state.processingStatus = action.payload.status;
      if (action.payload.progress !== undefined) {
        state.processingProgress = action.payload.progress;
      }
    },

    setDraftStatus: (state, action: PayloadAction<boolean>) => {
      state.isDraft = action.payload;
    },

    setHealthScribeState(state, action: PayloadAction<HealthScribeState>) {
      return action.payload;
    },

    // ==================== V2 Multi-Segment Actions ====================

    // Start a new segment (either first segment or resumed segment)
    startSegment: (
      state,
      action: PayloadAction<{
        sessionId: string;
        segmentId: string;
        segmentNumber: number;
        isResume: boolean;
        transcriptionSource?: "aws" | "heidi"; // NEW: Which STT backend
        heidiSessionId?: string; // NEW: Heidi's session ID
      }>
    ) => {
      state.isActive = true;
      state.isPaused = false;
      state.isPausing = false; // Clear isPausing flag when starting/resuming
      state.isLoading = false;
      state.isHidden = false; // Show the recording interface
      state.sessionId = action.payload.sessionId;
      state.currentSegmentId = action.payload.segmentId;
      state.currentSegmentNumber = action.payload.segmentNumber;
      state.currentSegmentTranscriptions = [];

      // Set transcription source and connection status
      state.transcriptionSource = action.payload.transcriptionSource || "aws";
      state.heidiSessionId = action.payload.heidiSessionId || null;

      // For Heidi, connectionStatus represents "ready to record", not WebSocket
      state.connectionStatus = "connected";

      // For Heidi transcription, start in "waiting" state
      if (action.payload.transcriptionSource === "heidi") {
        state.heidiTranscriptStatus = "waiting";
      } else {
        state.heidiTranscriptStatus = "idle";
      }

      // If it's NOT a resume (i.e., it's a fresh start), clear previous data
      if (!action.payload.isResume) {
        // Jon's note:
        // We originally had the following two lines,
        // but I believe these could cause data loss and there is no advantage to having them.
        // I am removing these dangerous lines.  If you think they are needed, contact Jon and Umer.
        // state.transcription = [];
        // state.segments = [];
      }

      // Always set startTime when starting a segment (fresh or resume)
      // Timer should show elapsed time for the CURRENT segment only
      state.startTime = new Date().toISOString();
    },

    // Set isPausing flag (for coordinating with recovery service)
    setIsPausing: (state, action: PayloadAction<boolean>) => {
      state.isPausing = action.payload;
    },

    // Pause current segment (stop recording, keep session open for resume)
    pauseSegment: (
      state,
      action: PayloadAction<{
        segmentData: ISegmentData;
      }>
    ) => {
      state.isActive = true; // Keep active to show Resume button instead of Start
      state.isPaused = true;
      state.isPausing = false; // Clear isPausing flag now that pause is complete
      state.segments.push(action.payload.segmentData);
      state.currentSegmentTranscriptions = [];
      state.currentSegmentId = null;
      state.connectionStatus = null;
    },

    // Add transcription to current segment
    addSegmentTranscription: (
      state,
      action: PayloadAction<ITranscriptionMessage>
    ) => {
      state.currentSegmentTranscriptions.push(action.payload);
      // Also add to global transcription for UI display
      state.transcription.push(action.payload);
    },

    // Complete entire session (finalize all segments, generate narrative)
    completeSession: (
      state,
      action: PayloadAction<{
        narrative: string;
        transcriptionId?: string | number;
      }>
    ) => {
      state.isActive = false;
      state.isPaused = false;
      state.noteToOverwrite = action.payload.narrative;
      state.transcriptionId =
        typeof action.payload.transcriptionId === "string"
          ? parseInt(action.payload.transcriptionId)
          : action.payload.transcriptionId || null;
      state.processingStatus = "completed";
      // Clear session data
      state.sessionId = null;
      state.currentSegmentId = null;
      state.currentSegmentNumber = 1;
      state.segments = [];
      state.currentSegmentTranscriptions = [];
      // state.transcription = []; // Intentionally permanently commented out so that we will keep transcript after AI Generate.  NPs want to keep it.
      state.connectionStatus = null;
      // Mark that user clicked Generate Narrative - future recordings should show "Resume" button
      state.hasClickedGenerateNarrative = true;
    },

    // Set hasClickedGenerateNarrative flag (for loading from draft note)
    setHasClickedGenerateNarrative: (state, action: PayloadAction<boolean>) => {
      state.hasClickedGenerateNarrative = action.payload;
    },

    // Reset segment state (for cleanup or errors)
    resetSegmentState: (state) => {
      state.isActive = false;
      state.isPaused = false;
      state.currentSegmentId = null;
      state.currentSegmentNumber = 1;
      state.segments = [];
      state.currentSegmentTranscriptions = [];
      state.sessionId = null;
      state.transcription = []; // ✅ Clear all transcriptions
    },

    // Load existing session (for resume/complete from session list)
    loadExistingSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        visitType: VisitType;
        segments: ISegmentData[];
        status: SessionStatus;
        patientId: string | number;
        patientName: string;
      }>
    ) => {
      state.sessionId = action.payload.sessionId;
      state.visitType = action.payload.visitType;
      state.segments = action.payload.segments;
      state.currentSegmentNumber = action.payload.segments.length + 1;
      // isPaused is true only for "paused" status; "active" sessions show as actively recording
      state.isPaused = action.payload.status === "paused";
      state.isActive = true; // Show Resume/Complete buttons
      state.patientId = action.payload.patientId;
      state.patientName = action.payload.patientName;
      state.currentSegmentId = null; // No active segment yet
      state.currentSegmentTranscriptions = [];
      state.isLoading = false;

      // Aggregate all transcriptions from all segments to display in UI
      // IMPORTANT: Filter out DISREGARD system messages to prevent duplication
      const DISREGARD_MESSAGE =
        "NOTE: DISREGARD ALL PREVIOUS TRANSCRIPT SEGMENTS, WHICH WERE ONLY A DRAFT. THE FINAL TRANSCRIPT OF THE CONVERSATION STARTS HERE:";
      const allTranscriptions: ITranscriptionMessage[] = [];
      action.payload.segments.forEach((segment) => {
        if (segment.transcriptions && segment.transcriptions.length > 0) {
          // Filter out DISREGARD messages
          const filteredTranscriptions = segment.transcriptions.filter(
            (trans) => trans.message !== DISREGARD_MESSAGE
          );
          allTranscriptions.push(...filteredTranscriptions);
        }
      });
      state.transcription = allTranscriptions;
    },

    // ==================== End V2 Actions ====================

    // Set the transcript from localStorage - MERGES with existing transcripts
    // localStorage is NEWER (just updated with new message), Redux is OLDER (not yet updated)
    setTranscriptFromLocalStorage: (
      state,
      action: PayloadAction<ITranscriptionMessage[]>
    ) => {
      const localStorageTranscripts = action.payload;
      // Convert Immer draft to plain objects before merging
      const existingTranscripts = current(state.transcription);

      // Use sequence merge: Redux (older) + localStorage (newer, just updated)
      const merged = mergeTranscriptions(
        existingTranscripts,
        localStorageTranscripts
      );

      state.transcription = merged;
    },

    // ==================== Heidi Progressive Transcription Actions ====================

    // Set Heidi transcript status (for UI display)
    setHeidiTranscriptStatus: (
      state,
      action: PayloadAction<
        | "idle"
        | "waiting"
        | "processing"
        | "transcribing"
        | "received"
        | "error"
      >
    ) => {
      state.heidiTranscriptStatus = action.payload;
      if (action.payload === "received") {
        state.lastTranscriptUpdateTime = new Date().toISOString();
      }
    },

    // Add transcriptions from progressive Heidi upload (APPEND-ONLY, with smart deduplication)
    addProgressiveTranscriptions: (
      state,
      action: PayloadAction<ITranscriptionMessage[]>
    ) => {
      // Normalize message for deduplication comparison
      // Handles: punctuation differences, capitalization, whitespace
      const normalizeForDedup = (msg: string): string => {
        return msg
          .trim()
          .toLowerCase()
          .replace(/[.,!?;:'"()[\]{}]/g, "") // Remove punctuation
          .replace(/\s+/g, " "); // Normalize whitespace to single spaces
      };

      // P1 FIX: Smart deduplication that only removes duplicates from recent messages
      // This prevents dropping legitimately repeated phrases (e.g., patient says
      // "I have a headache" at start and again 10 minutes later)
      // We only check for duplicates in the last 20 messages (roughly 1-2 chunks worth)
      const DEDUP_WINDOW_SIZE = 20;
      const recentMessages = state.transcription.slice(-DEDUP_WINDOW_SIZE);
      const recentMessageSet = new Set(
        recentMessages.map((msg) => normalizeForDedup(msg.message))
      );

      const newMessages = action.payload.filter((msg) => {
        const key = normalizeForDedup(msg.message);
        // Skip very short messages (likely noise)
        if (key.length < 3) {
          return false;
        }
        // Only deduplicate against recent messages (overlap detection)
        if (recentMessageSet.has(key)) {
          return false; // Skip duplicate in recent window
        }
        recentMessageSet.add(key);
        return true;
      });

      if (newMessages.length > 0) {
        state.transcription.push(...newMessages);
        state.currentSegmentTranscriptions.push(...newMessages);
      }

      state.heidiTranscriptStatus = "received";
      state.lastTranscriptUpdateTime = new Date().toISOString();
    },

    // Set Heidi session info
    setHeidiSessionInfo: (
      state,
      action: PayloadAction<{
        heidiSessionId: string;
        transcriptionSource: "heidi";
      }>
    ) => {
      state.heidiSessionId = action.payload.heidiSessionId;
      state.transcriptionSource = action.payload.transcriptionSource;
    },

    // ==================== End Heidi Actions ====================
  },
});

export const {
  initializeHealthScribe,
  startHealthScribe,
  healthScribeInitializationFailed,
  setIsPaused,
  stopHealthScribe,
  pauseAndUploadHealthScribe,
  hideHealthScribe,
  showHealthScribe,
  setVisitType,
  setPatientInfo,
  toggleClinicalSuggestions,
  setConnectionStatus,
  addTranscription,
  updateTranscription,
  clearTranscription,
  setNoteToOverwrite,
  setProcessingStatus,
  setDraftStatus,
  setHealthScribeState,
  // V2 Actions
  startSegment,
  setIsPausing,
  pauseSegment,
  addSegmentTranscription,
  completeSession,
  setHasClickedGenerateNarrative,
  resetSegmentState,
  loadExistingSession,
  setTranscriptFromLocalStorage,
  // Heidi Transcription Actions
  setHeidiTranscriptStatus,
  addProgressiveTranscriptions,
  setHeidiSessionInfo,
} = healthScribeSlice.actions;

export default healthScribeSlice.reducer;

// Selectors
export const selectCompleteTranscription = (state: {
  healthScribe: HealthScribeState;
}): string => {
  // Get only final (non-partial) transcription messages and combine them
  const finalMessages = state.healthScribe.transcription
    .filter((msg) => !msg.isPartial)
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  return finalMessages
    .map((msg) => `${msg.speaker}: ${msg.message}`)
    .join("\n");
};
